<template>
  <vs-popup 
    :active.sync="active"
    title="Create Storage Account"
    class="create-account-modal"
  >
    <div class="p-6">
      <form @submit.prevent="createAccount">
        <!-- User Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Select User *</label>
          <vs-select 
            v-model="form.user_id" 
            placeholder="Search and select user..."
            class="w-full"
            :state="errors.user_id ? 'danger' : null"
            filterable
            @search="searchUsers"
          >
            <vs-select-item 
              v-for="user in users" 
              :key="user.id" 
              :value="user.id" 
              :text="`${user.first_name} ${user.last_name} (${user.email})`" 
            />
          </vs-select>
          <small v-if="errors.user_id" class="text-danger">{{ errors.user_id }}</small>
        </div>

        <!-- Account Title -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Account Title *</label>
          <vs-input 
            v-model="form.account_title"
            placeholder="Enter account title..."
            class="w-full"
            :state="errors.account_title ? 'danger' : null"
          />
          <small v-if="errors.account_title" class="text-danger">{{ errors.account_title }}</small>
        </div>

        <!-- Quota -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Quota (GB) *</label>
          <vs-input 
            v-model="form.quota_gb"
            type="number"
            min="1"
            max="10000"
            placeholder="Enter quota in GB..."
            class="w-full"
            :state="errors.quota_gb ? 'danger' : null"
          />
          <small v-if="errors.quota_gb" class="text-danger">{{ errors.quota_gb }}</small>
          <small class="text-grey">Maximum allowed: 10,000 GB</small>
        </div>

        <!-- User Current Limits Info -->
        <div v-if="selectedUserLimits" class="mb-4 p-4 bg-primary-light rounded">
          <h4 class="text-sm font-medium mb-2">User Current Usage</h4>
          <div class="text-sm">
            <div>Accounts: {{ selectedUserLimits.current_usage.accounts_count }} / {{ selectedUserLimits.limits.max_accounts }}</div>
            <div>Total Quota: {{ selectedUserLimits.current_usage.total_quota_gb }} GB / {{ selectedUserLimits.limits.max_quota_gb }} GB</div>
          </div>
        </div>

        <!-- Send Email -->
        <div class="mb-6">
          <vs-checkbox v-model="form.send_email">
            Send welcome email to user
          </vs-checkbox>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-2">
          <vs-button 
            @click="$emit('close')"
            color="danger"
            type="border"
          >
            Cancel
          </vs-button>
          <vs-button 
            type="submit"
            color="primary"
            :loading="loading"
          >
            Create Account
          </vs-button>
        </div>
      </form>
    </div>
  </vs-popup>
</template>

<script>
import debounce from 'lodash/debounce'

export default {
  name: 'CreateAccountModal',
  data() {
    return {
      active: true,
      loading: false,
      users: [],
      selectedUserLimits: null,
      form: {
        user_id: '',
        account_title: '',
        quota_gb: 5,
        send_email: true
      },
      errors: {}
    }
  },
  watch: {
    'form.user_id'(newUserId) {
      if (newUserId) {
        this.loadUserLimits(newUserId)
      }
    },
    active(newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  mounted() {
    this.searchUsers('')
  },
  methods: {
    searchUsers: debounce(async function(query) {
      try {
        const response = await this.$http.get('/admin/users', {
          params: {
            search: query,
            per_page: 50
          }
        })
        
        if (response.data.status) {
          this.users = response.data.params.data || response.data.params
        }
      } catch (error) {
        console.error('Error searching users:', error)
      }
    }, 300),

    async loadUserLimits(userId) {
      try {
        const response = await this.$http.get(`/admin/haio-smart-storage/user-limits/${userId}`)
        if (response.data.status) {
          this.selectedUserLimits = response.data.params
        }
      } catch (error) {
        console.error('Error loading user limits:', error)
      }
    },

    async createAccount() {
      this.loading = true
      this.errors = {}

      try {
        const response = await this.$http.post('/admin/haio-smart-storage', this.form)
        
        if (response.data.status) {
          this.$vs.notify({
            title: 'Success',
            text: 'Storage account created successfully',
            color: 'success'
          })
          this.$emit('created')
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Validation errors
          const message = error.response.data.message
          this.$vs.notify({
            title: 'Validation Error',
            text: message,
            color: 'danger'
          })
          
          // Set field-specific errors if available
          if (error.response.data.errors) {
            this.errors = error.response.data.errors
          }
        } else {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to create storage account',
            color: 'danger'
          })
        }
      } finally {
        this.loading = false
      }
    },

    validateForm() {
      this.errors = {}
      
      if (!this.form.user_id) {
        this.errors.user_id = 'Please select a user'
      }
      
      if (!this.form.account_title) {
        this.errors.account_title = 'Account title is required'
      } else if (this.form.account_title.length < 3) {
        this.errors.account_title = 'Account title must be at least 3 characters'
      }
      
      if (!this.form.quota_gb || this.form.quota_gb < 1) {
        this.errors.quota_gb = 'Quota must be at least 1 GB'
      } else if (this.form.quota_gb > 10000) {
        this.errors.quota_gb = 'Quota cannot exceed 10,000 GB'
      }

      return Object.keys(this.errors).length === 0
    }
  }
}
</script>

<style scoped>
.create-account-modal {
  max-width: 500px;
}
</style>